import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import {Menu, X, Globe} from 'lucide-react';
import CareerModal from "./CareerModal";
import { useLanguage } from "../contexts/LanguageContext";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCareerModal, setShowCareerModal] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (href: string) => {
    if (href.startsWith('/')) {
      navigate(href);
    } else if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { name: t('nav.home'), href: "#home" },
    { name: t('nav.about'), href: "#about" },
    { name: t('nav.services'), href: "#services" },
    { name: "Clientes", href: "/clientes" },
    { name: t('nav.contact'), href: "#contact" }
  ];

  const handleCareerClick = () => {
    setShowCareerModal(true);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-4' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <a href="#" className="flex flex-col items-center">
              <img
                src="https://static.lumi.new/d2/d2420d10297c1cc2cf7868802e04f7fd.webp"
                alt="Pinhacorada Logo"
                className="h-16 w-auto"
              />
              <span className={`text-sm font-bold mt-1 ${scrolled ? 'text-green-700' : 'text-white'}`}>PINHACORADA</span>
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-medium transition-colors hover:text-green-600 ${
                  scrolled ? "text-gray-700" : "text-white/90 hover:text-white"
                } ${location.pathname === link.href ? 'text-green-600 font-semibold' : ''}`}
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={handleCareerClick}
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                scrolled ? "text-gray-700" : "text-white/90 hover:text-white"
              }`}
            >
              {t('nav.careers')}
            </button>
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-green-600 ${
                scrolled ? "text-gray-700" : "text-white/90 hover:text-white"
              }`}
              title={language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            >
              <Globe className="h-4 w-4" />
              <span className="uppercase">{language === 'pt' ? 'EN' : 'PT'}</span>
            </button>
            <a
              href="#contact"
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all transform hover:scale-105 ${
                scrolled
                  ? "bg-green-800 text-white hover:bg-green-900"
                  : "bg-white text-green-900 hover:bg-gray-100"
              }`}
            >
              {t('nav.getQuote')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md transition-colors ${scrolled ? 'text-gray-800' : 'text-white'}`}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-100 animate-fade-in-down">
          <div className="px-4 pt-2 pb-6 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => { setIsOpen(false); handleNavClick(link.href); }}
                className="block w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:text-green-700 hover:bg-green-50 rounded-lg"
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={() => {
                setIsOpen(false);
                setShowCareerModal(true);
              }}
              className="block w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:text-green-700 hover:bg-green-50 rounded-lg"
            >
              {t('nav.careers')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                toggleLanguage();
              }}
              className="flex items-center gap-2 w-full px-3 py-3 text-base font-medium text-gray-700 hover:text-green-700 hover:bg-green-50 rounded-lg"
            >
              <Globe className="h-5 w-5" />
              <span>{language === 'pt' ? 'English' : 'Português'}</span>
            </button>
          </div>
        </div>
      )}

      <CareerModal isOpen={showCareerModal} onClose={() => setShowCareerModal(false)} />
    </nav>
  );
}